import { Link } from "react-router";
import { generateMetaTags, SITE_URL } from "~/lib/seo";

export function meta() {
	return generateMetaTags({
		title: "Ochrana soukromí – FirmIndex",
		description:
			"Zásady ochrany osobních údajů služby FirmIndex. Zjistěte, jaké údaje zpracováváme a jaká máte práva.",
		canonical: `${SITE_URL}/ochrana-soukromi`,
	});
}

export default function OchranaSoukromi() {
	return (
		<section className="px-6 py-16 lg:px-8 lg:py-24">
			<article className="prose prose-gray mx-auto max-w-3xl">
				<h1 className="text-4xl font-extrabold tracking-tight text-text">Ochrana soukromí</h1>
				<p className="mt-2 text-sm text-muted">Poslední aktualizace: 19. března 2026</p>

				<div className="mt-12 space-y-8 text-sm leading-relaxed text-muted">
					<section>
						<h2 className="text-xl font-bold text-text">1. Správce osobních údajů</h2>
						<p>
							Správcem osobních údajů je společnost Recadev s.r.o., IČO: 07654090, se sídlem U
							Plesenky 3353/15, Martinov, 723 00 Ostrava, Česká republika, provozovatel služby
							FirmIndex (dále jen „Správce").
						</p>
						<p>Kontakt pro záležitosti ochrany osobních údajů: privacy@firmindex.cz</p>
					</section>

					<section>
						<h2 className="text-xl font-bold text-text">2. Jaké údaje zpracováváme</h2>
						<p>V rámci poskytování Služby zpracováváme následující kategorie osobních údajů:</p>

						<h3 className="mt-4 text-lg font-semibold text-text">2.1 Údaje uživatelů Služby</h3>
						<ul className="mt-2 list-disc space-y-1 pl-6">
							<li>Identifikační údaje (jméno, příjmení)</li>
							<li>Kontaktní údaje (e-mailová adresa)</li>
							<li>Přihlašovací údaje (heslo v šifrované podobě)</li>
							<li>Údaje o využívání Služby (historie vyhledávání, přístupy)</li>
							<li>Technické údaje (IP adresa, typ prohlížeče, operační systém)</li>
							<li>Platební údaje (zpracovávány prostřednictvím platebního poskytovatele Stripe)</li>
						</ul>

						<h3 className="mt-4 text-lg font-semibold text-text">
							2.2 Údaje z veřejných rejstříků
						</h3>
						<p>
							Za účelem poskytování finanční analýzy a hodnocení rizik českých obchodních
							společností zpracováváme údaje získané z následujících veřejně přístupných zdrojů:
						</p>
						<ul className="mt-2 list-disc space-y-1 pl-6">
							<li>
								<strong>Obchodní rejstřík</strong> (justice.cz) — základní údaje o společnostech,
								statutárních orgánech a společnících
							</li>
							<li>
								<strong>Sbírka listin</strong> (justice.cz) — účetní závěrky a výroční zprávy
								zákonem povinně zveřejňované
							</li>
							<li>
								<strong>ARES</strong> (Administrativní registr ekonomických subjektů, Ministerstvo
								financí) — identifikační údaje, registrace DPH, právní forma
							</li>
							<li>
								<strong>Insolvenční rejstřík (ISIR)</strong> (Ministerstvo spravedlnosti) — údaje o
								insolvečních řízeních
							</li>
							<li>
								<strong>Registr ekonomických subjektů (RES)</strong> (Český statistický úřad) —
								klasifikace CZ-NACE, kategorie počtu zaměstnanců
							</li>
							<li>
								<strong>Registr smluv</strong> (smlouvy.gov.cz, Digitální a informační agentura) —
								smlouvy se státem
							</li>
							<li>
								<strong>Věstník veřejných zakázek a Registr veřejných zakázek</strong> (MMR ČR, ISVZ
								open data) a <strong>TED</strong> (Úřad pro publikace EU) — veřejné zakázky
							</li>
							<li>
								<strong>Registr dotací IS ReD</strong> (Generální finanční ředitelství),{" "}
								<strong>SZIF</strong> a <strong>seznamy operací EU fondů</strong> (MMR ČR) — dotace
							</li>
						</ul>
						<p className="mt-2">
							Z těchto rejstříků zpracováváme zejména: jména a příjmení statutárních orgánů a
							společníků, data narození (pouze pro disambiguaci osob, nejsou zobrazována koncovým
							uživatelům), adresy, funkce, data jmenování a odvolání, výše podílů a údaje z účetních
							závěrek.
						</p>
					</section>

					<section>
						<h2 className="text-xl font-bold text-text">3. Účely zpracování</h2>
						<p>Vaše osobní údaje zpracováváme za těmito účely:</p>
						<ul className="mt-2 list-disc space-y-1 pl-6">
							<li>Poskytování a správa Služby (registrace, správa účtu, zákaznická podpora)</li>
							<li>
								Finanční analýza a hodnocení rizik obchodních společností na základě veřejně
								dostupných dat
							</li>
							<li>Komunikace s uživateli (transakční e-maily, upozornění na změny)</li>
							<li>Zpracování plateb a správa předplatného</li>
							<li>Zlepšování Služby a uživatelského zážitku</li>
							<li>Plnění právních povinností</li>
						</ul>
					</section>

					<section>
						<h2 className="text-xl font-bold text-text">4. Právní základ zpracování</h2>
						<p>Osobní údaje zpracováváme na základě těchto právních titulů dle GDPR:</p>
						<ul className="mt-2 list-disc space-y-1 pl-6">
							<li>
								<strong>Plnění smlouvy</strong> (čl. 6 odst. 1 písm. b) GDPR) — zpracování údajů
								uživatelů nezbytných pro poskytování Služby (registrace, správa účtu, zpracování
								plateb)
							</li>
							<li>
								<strong>Oprávněný zájem Správce</strong> (čl. 6 odst. 1 písm. f) GDPR) — zpracování
								veřejně dostupných údajů z veřejných rejstříků (Obchodní rejstřík, Sbírka listin,
								ARES, ISIR, RES) za účelem finanční analýzy a hodnocení rizik obchodních
								společností. Data jsou zveřejněna na základě zákona č. 304/2013 Sb., o veřejných
								rejstřících právnických a fyzických osob, a dalších zákonů upravujících veřejnost
								těchto rejstříků. Subjekty údajů jsou osoby, jejichž údaje jsou ze zákona povinně
								zveřejňovány (statutární orgány, společníci). Oprávněný zájem spočívá v poskytování
								služby finanční analýzy a transparentnosti obchodních vztahů.
							</li>
							<li>
								<strong>Souhlas</strong> (čl. 6 odst. 1 písm. a) GDPR) — marketingová komunikace,
								analytické cookies
							</li>
							<li>
								<strong>Plnění právních povinností</strong> (čl. 6 odst. 1 písm. c) GDPR) — vedení
								účetnictví, daňové povinnosti
							</li>
						</ul>
					</section>

					<section>
						<h2 className="text-xl font-bold text-text">5. Doba uchování</h2>
						<p>
							Osobní údaje uchováváme po dobu nezbytně nutnou pro dosažení účelu zpracování.
							Konkrétní doby uchování se liší podle typu údajů:
						</p>
						<ul className="mt-2 list-disc space-y-1 pl-6">
							<li>
								<strong>Údaje uživatelského účtu</strong> — po dobu existence účtu a 30 dní po jeho
								smazání na žádost uživatele (pro případné obnovení)
							</li>
							<li>
								<strong>Fakturační a platební údaje</strong> — 10 let od uskutečnění platby (zákonná
								povinnost dle zákona o účetnictví)
							</li>
							<li>
								<strong>E-mailové protokoly</strong> — 180 dní od odeslání
							</li>
							<li>
								<strong>Provozní protokoly a diagnostika</strong> — 90 až 365 dní dle typu záznamu
							</li>
							<li>
								<strong>Údaje ze veřejných rejstříků</strong> (statutární orgány, společníci) — po
								dobu, po kterou zůstávají zveřejněny v příslušném veřejném rejstříku. Záznamy o
								osobách, které již nejsou aktivní (odvolaní jednatelé, bývalí společníci), jsou
								označeny jako neaktivní, ale uchovávány pro účely historické analýzy a kontinuity
								obchodních vztahů.
							</li>
							<li>
								<strong>Veřejně dostupné finanční údaje</strong> (účetní závěrky) — bez časového
								omezení, jedná se o veřejně přístupné informace ze Sbírky listin
							</li>
							<li>
								<strong>Žádosti subjektů údajů (SAR)</strong> — 3 roky od vyřízení žádosti
							</li>
						</ul>
						<p className="mt-2">
							Po uplynutí příslušné doby údaje mažeme nebo anonymizujeme podle dokumentovaného
							retenčního postupu. Údaje, jejichž přesnost je zpochybněna, do vyřízení podnětu
							omezíme a nezobrazujeme je zákazníkům.
						</p>
					</section>

					<section>
						<h2 className="text-xl font-bold text-text">6. Vaše práva</h2>
						<p>Jako subjekt údajů máte následující práva:</p>
						<ul className="mt-2 list-disc space-y-1 pl-6">
							<li>
								<strong>Právo na přístup</strong> (čl. 15 GDPR) — můžete požádat o informaci, jaké
								údaje o vás zpracováváme
							</li>
							<li>
								<strong>Právo na opravu</strong> (čl. 16 GDPR) — v případě nepřesnosti údajů
							</li>
							<li>
								<strong>Právo na výmaz</strong> (čl. 17 GDPR) — právo být zapomenut, s výhradou
								zákonných povinností a oprávněného zájmu
							</li>
							<li>
								<strong>Právo na omezení zpracování</strong> (čl. 18 GDPR)
							</li>
							<li>
								<strong>Právo na přenositelnost</strong> (čl. 20 GDPR) — u údajů zpracovávaných na
								základě smlouvy nebo souhlasu
							</li>
							<li>
								<strong>Právo vznést námitku</strong> (čl. 21 GDPR) — zejména proti zpracování na
								základě oprávněného zájmu, včetně zpracování údajů z veřejných rejstříků
							</li>
							<li>
								<strong>Právo podat stížnost</strong> u dozorového úřadu — Úřad pro ochranu osobních
								údajů (ÚOOÚ), Pplk. Sochora 27, 170 00 Praha 7, www.uoou.cz
							</li>
						</ul>
						<p className="mt-2">
							Pro uplatnění svých práv nás kontaktujte na privacy@firmindex.cz. Žádosti vyřizujeme
							bez zbytečného odkladu, nejpozději do 30 dnů od přijetí.
						</p>
						<p className="mt-2">
							Nesprávný údaj lze nahlásit také přímo odkazem „Nahlásit nesprávný údaj“ u příslušného
							záznamu. Podnět evidujeme s historií jeho vyřízení; dotčený rizikový údaj do prověření
							omezíme. Omezení platí pro všechny zákaznické tarify včetně placených.
						</p>
						<p className="mt-2">
							<strong>Upozornění pro osoby uvedené ve veřejných rejstřících:</strong> Pokud jsou
							vaše údaje zveřejněny v Obchodním rejstříku nebo jiném veřejném rejstříku na základě
							zákona, právo na výmaz může být omezeno po dobu, po kterou údaje zůstávají ve veřejném
							rejstříku. V takovém případě posoudíme vaši žádost individuálně s ohledem na vyvážení
							oprávněného zájmu a vašich práv.
						</p>
					</section>

					<section>
						<h2 className="text-xl font-bold text-text">7. Cookies</h2>
						<p>
							Naše webová stránka používá cookies pro zajištění správného fungování, analýzu
							návštěvnosti a personalizaci obsahu. Podrobné informace o cookies a možnostech jejich
							nastavení naleznete v{" "}
							<Link to="/cookie-policy" className="underline hover:text-primary">
								Zásadách používání cookies
							</Link>
							.
						</p>
					</section>

					<section>
						<h2 className="text-xl font-bold text-text">8. Předávání údajů do třetích zemí</h2>
						<p>
							Infrastruktura Služby je provozována primárně v Evropské unii (Google Cloud Platform,
							region europe-west3, Frankfurt). Někteří zpracovatelé mohou předávat údaje mimo EU/EHP
							na základě standardních smluvních doložek (čl. 46 odst. 2 písm. c) GDPR) nebo
							rozhodnutí o odpovídající ochraně (čl. 45 GDPR). Podrobné informace o zárukách
							poskytnou jednotliví zpracovatelé na vyžádání.
						</p>
					</section>

					<section>
						<h2 className="text-xl font-bold text-text">9. Zpracovatelé osobních údajů</h2>
						<p>
							Pro zajištění provozu Služby využíváme následující zpracovatele (sub-procesory) ve
							smyslu čl. 28 GDPR:
						</p>
						<ul className="mt-2 list-disc space-y-1 pl-6">
							<li>
								<strong>Google Cloud Platform</strong> (cloud.google.com) — infrastruktura, ukládání
								dat a výpočetní kapacita
							</li>
							<li>
								<strong>Supabase</strong> (supabase.com) — autentizace a správa uživatelů
							</li>
							<li>
								<strong>Stripe</strong> (stripe.com) — zpracování plateb
							</li>
							<li>
								<strong>Sentry</strong> (sentry.io) — monitorování chyb a diagnostika
							</li>
							<li>
								<strong>Resend</strong> (resend.com) — doručování transakčních e-mailů
							</li>
							<li>
								<strong>Anthropic</strong> (anthropic.com) — zpracování finančních dokumentů pomocí
								AI pro účely extrakce strukturovaných dat z účetních závěrek
							</li>
						</ul>
						<p className="mt-2">
							Se všemi zpracovateli máme uzavřeny smlouvy zajišťující ochranu vašich údajů v souladu
							s GDPR.
						</p>
					</section>

					<section>
						<h2 className="text-xl font-bold text-text">10. Zabezpečení</h2>
						<p>
							Přijímáme vhodná technická a organizační opatření k ochraně osobních údajů před
							neoprávněným přístupem, změnou, zveřejněním nebo zničením. To zahrnuje šifrování dat
							při přenosu (TLS), šifrování hesel a pravidelné bezpečnostní audity.
						</p>
					</section>

					<section>
						<h2 className="text-xl font-bold text-text">11. Změny zásad</h2>
						<p>
							Tyto zásady ochrany soukromí můžeme příležitostně aktualizovat. O významných změnách
							vás budeme informovat prostřednictvím e-mailu nebo oznámení ve Službě.
						</p>
					</section>
				</div>
			</article>
		</section>
	);
}
